"use client";

// The app's ONE source of the reduced-motion preference — and, more
// importantly, an SSR-safe one. Nothing in this module may let the preference
// reach the first client render.
//
// Why framer-motion's own useReducedMotion() cannot be used (measured against
// the installed 13.2.0, not assumed):
//
//   function useReducedMotion() {
//     !hasReducedMotionListener.current && initPrefersReducedMotion();
//     const [shouldReduceMotion] = useState(prefersReducedMotion.current);
//     return shouldReduceMotion;
//   }
//
//   // motion-dom/dist/es/render/utils/reduced-motion/state.mjs
//   const prefersReducedMotion = { current: null };   // null server-side
//
// `initPrefersReducedMotion()` runs *during the first render* and assigns the
// live `window.matchMedia("(prefers-reduced-motion)")` result into that
// module-level global. So the hook returns `null` on the server and `true` on
// a reduced-motion client's very first render — and framer writes `initial`
// straight into the markup. Verified with react-dom/server:
//
//   initial={{ opacity: 0, y: 22, scale: 0.99 }}
//     → <div style="opacity:0;transform:translateY(22px) scale(0.99)">
//   initial={{ opacity: 0 }}                      (the reduced variant)
//     → <div style="opacity:0">
//
// Two different `style` attributes for the same element: a hydration mismatch
// that only fires for people who have reduced motion switched on, which is why
// it survives review. The `?? false` guard that used to sit on every call site
// normalised the *value* but not the divergence — the server rendered `false`
// while the client's first render produced `true`.
//
// useSyncExternalStore is the fix, and it is already this repo's proven pattern
// (see useFinePointer in landing.tsx). React uses `getServerSnapshot` for the
// hydration render and only then swaps to `getSnapshot`, so the first client
// paint is byte-identical to the server's — and a reduced-motion user's UI
// settles to the still version one commit later, which the global
// `prefers-reduced-motion` rule in globals.css has already zeroed the
// transitions for.

import { useSyncExternalStore } from "react";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function hasMatchMedia(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function subscribe(onStoreChange: () => void): () => void {
  if (!hasMatchMedia()) return () => {};
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function getSnapshot(): boolean {
  return hasMatchMedia() ? window.matchMedia(REDUCED_MOTION_QUERY).matches : false;
}

/**
 * The fixed value the server renders. `false` is the honest default: it is what
 * the server-rendered motion attributes were computed from, and CSS gives a
 * reduced-motion visitor the still version regardless of JS.
 */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * True when the visitor asks for reduced motion — false on the server and for
 * the whole of the first client render, so it can never move the markup.
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
